/**
 * RENDER module — owns renderer, scene, camera, fog, and the noir post
 * pipeline (selective bloom → tone map → B&W grade + grain + vignette).
 * Owned by RENDER; see CONTRACTS.md.
 */
import {
  PCFSoftShadowMap,
  PerspectiveCamera,
  ReinhardToneMapping,
  Scene,
  WebGLRenderer
} from 'three'
import type { NoirRenderConfig, RenderHandles } from '../core/types'
import { toPostConfig } from './config'
import { FAR_PLANE, FIELD_OF_VIEW_DEG, MS_PER_SECOND, NEAR_PLANE } from './constants'
import { installRenderDebug } from './debug'
import { createNoirPipeline } from './pipeline'

export const initRender = (container: HTMLElement, config: NoirRenderConfig): RenderHandles => {
  const renderer = new WebGLRenderer({ antialias: true })
  renderer.setSize(window.innerWidth, window.innerHeight)
  renderer.shadowMap.enabled = true
  renderer.shadowMap.type = PCFSoftShadowMap
  renderer.toneMapping = ReinhardToneMapping
  container.appendChild(renderer.domElement)

  const scene = new Scene()
  const camera = new PerspectiveCamera(
    FIELD_OF_VIEW_DEG,
    window.innerWidth / window.innerHeight,
    NEAR_PLANE,
    FAR_PLANE
  )

  // Pipeline owns fog, pixel ratio, tone-mapping exposure, and all post
  // passes; every NoirRenderConfig knob flows through it (no duplicates).
  const pipeline = createNoirPipeline(renderer, scene, camera, toPostConfig(config))

  window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight
    camera.updateProjectionMatrix()
    pipeline.setSize(window.innerWidth, window.innerHeight)
  })

  installRenderDebug(pipeline)

  const startedAtMs = performance.now()

  return {
    scene,
    camera,
    renderer,
    render(): void {
      const elapsedSeconds = (performance.now() - startedAtMs) / MS_PER_SECOND
      pipeline.render(elapsedSeconds)
    },
    setConfig(partial): void {
      pipeline.setConfig(partial)
    }
  }
}
